import React, { forwardRef, useState } from 'react';
import { CustomModal } from '@components';
import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { apiCreateMarketBidAsk, apiGetMarketplace } from '@store';
import { showToast } from '@utils';

const CreateTrade = forwardRef(({ handleClose, contracts }, ref) => {
    const dispatch = useDispatch();
    const initialState = {
        contract: '',
        price: '',
        quantity: '',
        ttl: '',
        type: '',
        location: 'SEA',
        currency: '',
        option: ''
    };

    const [state, setState] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isPositiveInteger = (value) => {
        const trimmed = String(value).trim();
        return /^\d+$/.test(trimmed) && parseInt(trimmed, 10) >= 1;
    };

    // Allows an optional integer part followed by at most 3 decimal places
    const hasValidDecimalPlaces = (value) => /^\d*\.?\d{0,3}$/.test(value);

    const validateForm = () => {
        const newErrors = {};

        // Validate contract
        if (!state.contract || state.contract.trim() === '') {
            newErrors.contract = 'Contract is required';
        }

        // Validate price
        if (!state.price || state.price === '') {
            newErrors.price = 'Price is required';
        } else if (parseFloat(state.price) <= 0) {
            newErrors.price = 'Price must be greater than 0';
        }

        // Validate quantity
        if (!state.quantity || state.quantity === '') {
            newErrors.quantity = 'Max Volume is required';
        } else if (parseFloat(state.quantity) <= 0) {
            newErrors.quantity = 'Max Volume must be greater than 0';
        }

        // Validate TTL - must be an integer of at least 1
        if (state.ttl === '' || state.ttl === null || state.ttl === undefined) {
            newErrors.ttl = 'TTL is required';
        } else if (!isPositiveInteger(state.ttl)) {
            newErrors.ttl = 'TTL must be a number of at least 1';
        }

        // Validate type (Bid/Ask)
        if (!state.type || state.type.trim() === '') {
            newErrors.type = 'Bid/Ask is required';
        }

        // Validate option
        if (!state.option || state.option.trim() === '') {
            newErrors.option = 'Order is required';
        }

        // Validate currency
        if (!state.currency || state.currency.trim() === '') {
            newErrors.currency = 'Currency is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const isFormValid = () => {
        return (
            state.contract &&
            state.price &&
            parseFloat(state.price) > 0 &&
            state.quantity &&
            parseFloat(state.quantity) > 0 &&
            isPositiveInteger(state.ttl) &&
            state.type &&
            state.option &&
            state.currency
        );
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        // Price and quantity are limited to at most 3 decimal places
        if (
            (name === 'price' || name === 'quantity') &&
            value !== '' &&
            !hasValidDecimalPlaces(value)
        ) {
            return;
        }
        setState({
            ...state,
            [name]: value
        });
        // TTL is validated live so an out of range value is flagged immediately
        if (name === 'ttl') {
            setErrors({
                ...errors,
                ttl:
                    value === '' || isPositiveInteger(value)
                        ? ''
                        : 'TTL must be a number of at least 1'
            });
            return;
        }
        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: ''
            });
        }
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        const newErrors = { ...errors };

        if (name === 'price') {
            if (!value || value === '') {
                newErrors.price = 'Price is required';
            } else if (parseFloat(value) <= 0) {
                newErrors.price = 'Price must be greater than 0';
            } else {
                newErrors.price = '';
            }
        }

        if (name === 'quantity') {
            if (!value || value === '') {
                newErrors.quantity = 'Max Volume is required';
            } else if (parseFloat(value) <= 0) {
                newErrors.quantity = 'Max Volume must be greater than 0';
            } else {
                newErrors.quantity = '';
            }
        }

        if (name === 'ttl') {
            if (value === '' || value === null || value === undefined) {
                newErrors.ttl = 'TTL is required';
            } else if (!isPositiveInteger(value)) {
                newErrors.ttl = 'TTL must be a number of at least 1';
            } else {
                newErrors.ttl = '';
            }
        }

        if (name === 'contract') {
            newErrors.contract =
                !value || value.trim() === '' ? 'Contract is required' : '';
        }

        if (name === 'type') {
            newErrors.type =
                !value || value.trim() === '' ? 'Bid/Ask is required' : '';
        }

        if (name === 'option') {
            newErrors.option =
                !value || value.trim() === '' ? 'Type is required' : '';
        }

        if (name === 'currency') {
            newErrors.currency =
                !value || value.trim() === '' ? 'Currency is required' : '';
        }

        setErrors(newErrors);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isSubmitting) return;

        // Validate form before submission
        if (!validateForm()) {
            showToast('error', 'Please fill all required fields correctly');
            return;
        }

        const data = {
            ...state,
            price: parseFloat(state.price),
            quantity: parseFloat(state.quantity),
            ttl: parseInt(state.ttl, 10)
        };

        setIsSubmitting(true);
        try {
            const resp = await dispatch(apiCreateMarketBidAsk(data));
            const entryLabel = state.type === 'bid' ? 'Bid' : 'Ask';
            if (resp?.meta?.requestStatus === 'fulfilled') {
                showToast(
                    'success',
                    `${entryLabel} entry created successfully`
                );
                dispatch(apiGetMarketplace());
                handleClose();
            } else if (resp?.meta?.requestStatus === 'rejected') {
                showToast(
                    'error',
                    resp?.payload || `Failed to create ${entryLabel} entry`
                );
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <CustomModal
            handleClose={handleClose}
            handleSubmit={handleSubmit}
            modalTitle={`Create New Trade`}
            buttonText={isSubmitting ? 'Submitting...' : 'Submit'}
            disabled={!isFormValid() || isSubmitting}
            ref={ref}
        >
            <Box className="flex flex-col sm:flex-row gap-3">
                <Box className="flex flex-col gap-2 flex-1 min-w-0">
                    <FormControl error={!!errors.contract}>
                        <InputLabel id="demo-simple-select-label">
                            Contract *
                        </InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label="Contract"
                            name="contract"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={state?.contract}
                            required
                        >
                            {contracts?.map((name, index) => (
                                <MenuItem key={index} value={name}>
                                    {name}
                                </MenuItem>
                            ))}
                        </Select>
                        {errors.contract && (
                            <span className="text-red-500 text-xs">
                                {errors.contract}
                            </span>
                        )}
                    </FormControl>
                    <FormControl error={!!errors.price}>
                        <InputLabel id="priceLabel">Price *</InputLabel>
                        <OutlinedInput
                            id="price"
                            label="Price"
                            name="price"
                            type="number"
                            //non negative and non zero number, up to 3 decimal places
                            inputProps={{ min: 0, step: 0.001 }}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            onKeyDown={(e) => {
                                if (['-', '+', 'e', 'E'].includes(e.key)) {
                                    e.preventDefault();
                                }
                            }}
                            value={state.price}
                            required
                        />
                        {errors.price && (
                            <span className="text-red-500 text-xs">
                                {errors.price}
                            </span>
                        )}
                    </FormControl>
                    <FormControl error={!!errors.type}>
                        <InputLabel id="demo-simple-select-label">
                            Bid/Ask *
                        </InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label="bid/ask"
                            name="type"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={state.type}
                            required
                        >
                            <MenuItem value="bid">Bid</MenuItem>
                            <MenuItem value="ask">Ask</MenuItem>
                        </Select>
                        {errors.type && (
                            <span className="text-red-500 text-xs">
                                {errors.type}
                            </span>
                        )}
                    </FormControl>
                    <FormControl error={!!errors.option}>
                        <InputLabel id="demo-simple-select-label">
                            Type *
                        </InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label="bid/ask"
                            name="option"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={state.option}
                            required
                        >
                            <MenuItem value="AON">All Or None</MenuItem>
                            <MenuItem value="POK">Partial Fill Ok</MenuItem>
                        </Select>
                        {errors.option && (
                            <span className="text-red-500 text-xs">
                                {errors.option}
                            </span>
                        )}
                    </FormControl>
                </Box>
                <Box className="flex flex-col gap-2 flex-1 min-w-0">
                    <FormControl error={!!errors.quantity}>
                        <InputLabel id="maxVolumeLabel">
                            Max Volume (MW) *
                        </InputLabel>
                        <OutlinedInput
                            id="max_volume"
                            label="Max Volume (MW)"
                            name="quantity"
                            type="number"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            onKeyDown={(e) => {
                                if (['-', '+', 'e', 'E'].includes(e.key)) {
                                    e.preventDefault();
                                }
                            }}
                            value={state.quantity}
                            inputProps={{ min: 0.1, step: 0.001 }}
                        />
                        {errors.quantity && (
                            <span className="text-red-500 text-xs">
                                {errors.quantity}
                            </span>
                        )}
                    </FormControl>
                    <FormControl error={!!errors.ttl}>
                        <InputLabel id="maxVolumeLabel">
                            TTL (Min)
                        </InputLabel>
                        <OutlinedInput
                            id="ttl"
                            label="TTL (Min)"
                            name="ttl"
                            type="number"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            onKeyDown={(e) => {
                                if (
                                    ['-', ',', '+', '.', 'e', 'E'].includes(
                                        e.key
                                    )
                                ) {
                                    e.preventDefault();
                                }
                            }}
                            value={state.ttl}
                            required
                            inputProps={{ min: 1, step: 1 }}
                        />
                        {errors.ttl && (
                            <span className="text-red-500 text-xs">
                                {errors.ttl}
                            </span>
                        )}
                    </FormControl>
                    <FormControl>
                        <InputLabel id="demo-simple-select-label">
                            Location *
                        </InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label="location"
                            name="location"
                            onChange={handleChange}
                            value={state.location}
                            required
                        >
                            <MenuItem value="SEA">SEA</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl error={!!errors.currency}>
                        <InputLabel id="demo-simple-select-label">
                            Currency *
                        </InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label="currency"
                            name="currency"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={state.currency}
                            required
                        >
                            <MenuItem value="EUR">EUR</MenuItem>
                            <MenuItem value="CHF">CHF</MenuItem>
                        </Select>
                        {errors.currency && (
                            <span className="text-red-500 text-xs">
                                {errors.currency}
                            </span>
                        )}
                    </FormControl>
                </Box>
            </Box>
        </CustomModal>
    );
});

export default CreateTrade;
